import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  EnableSocialAdsRequest,
  SocialConnection,
  SocialConnectionList,
  SocialProvider,
} from '@nucleus-commerce/api-client/admin';

import { socialApi } from '../api/social-api';

import { socialKeys } from './social-keys';

/** Both ads calls answer the updated connection; it replaces its row in the cached list. */
function settle(queryClient: QueryClient, storeId: string, connection: SocialConnection) {
  queryClient.setQueryData<SocialConnectionList>(socialKeys.connections(storeId), (list) =>
    list ? { items: list.items.map((row) => (row.id === connection.id ? connection : row)) } : list,
  );
  return queryClient.invalidateQueries({ queryKey: socialKeys.connections(storeId) });
}

/** Mints the Conversions API token and connects the analytics destination for the account. */
export function useEnableAds(storeId: string, provider: SocialProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EnableSocialAdsRequest) => socialApi.enableAds(storeId, provider, body),
    meta: { successMessage: 'social:toasts.adsEnabled', silentError: true },
    onSuccess: (connection) => settle(queryClient, storeId, connection),
  });
}

/** Forgets the ad account and removes the destination; the scopes stay for a later enable. */
export function useDisableAds(storeId: string, provider: SocialProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => socialApi.disableAds(storeId, provider),
    meta: { successMessage: 'social:toasts.adsDisabled' },
    onSuccess: (connection) => settle(queryClient, storeId, connection),
  });
}
